/**
 * The definitions listing: a dense table of what this program DEFINES.
 *
 * Its columns come from the registry (`/api/schema`), not from a list written
 * here, for the reason the whole registry exists: a field added to the part kind
 * appears without this file being touched. Only scalar fields become columns -
 * the listing endpoint leaves references and lists out to save a query per row,
 * and a column that is always blank is worse than no column.
 *
 * MULTIPLICITY is deliberately not among them, and that is a modelling point
 * rather than a layout one: it characterises a USAGE inside its parent, so a
 * definition has none to state. `columnsFor` drops it, and it stays where it is a
 * fact - the `contains[]` rows and the tree.
 *
 * A derived field is labelled read-only in its header. Nothing on this table is
 * edited in place - a click selects and a DOUBLE-CLICK opens the full editor - so
 * that is not a warning about this screen; it is the fact that the write path
 * REFUSES a derived field, said in the one place somebody sees the field's name.
 *
 * The role column is the point of the table. Definitions and uncontained
 * instances sit here together - dropping the instances would lose the design's
 * root - so each row says which it is rather than leaving it to be inferred from
 * whether `Type` happens to be filled in, which is exactly what the generic
 * table did.
 */

import Chip from '@mui/material/Chip'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { FieldSchema, KindSchema } from '../../schema'
import { MONOSPACE } from '../../theme'
import HelpTip from '../common/HelpTip'
import { useFittedHeight } from '../common/grid-layout'
import { unitLabel } from '../budgets/budget-model'
import { splitQuantity } from '../entity/quantity'
import { columnsFor } from './parts-model'
import type { ListedPart, PartColumn } from './parts-model'

export const GENERIC_HELP =
  'A generic kind: it may be specialised by other definitions, but never used ' +
  'directly, so nothing offers it as the type of a part.'

export const INSTANCE_HELP =
  'A usage, not a definition, and nothing contains it — the design’s root is one ' +
  'of these. It is listed here because no other pane would show it.'

/** A cell's text, with a boolean field read as a word rather than 'false'. */
export function cellText(value: unknown, field: FieldSchema): string {
  if (field.kind === 'flag' || field.kind === 'conjugate') return value === true ? 'yes' : ''
  const text = String(value ?? '')
  // A QUANTITY reads as '40 kg', not '40 [SI::kg]'. The stored form is the
  // model's syntax and its namespace, and a column of it is a column of
  // punctuation. Only quantities: a type name contains '::' and means it.
  if (field.kind === 'quantity' && text) {
    const { value: figure, unit } = displayQuantity(text)
    return unit ? `${figure} ${unit}` : figure
  }
  return text
}

/**
 * A stored quantity, ready to read: `-20 [SI::'°C_abs']` → `-20` and `°C`.
 *
 * The brackets are the model's syntax and the package is its namespace; neither
 * is information to somebody reading a column of masses.
 *
 * IT DOES NOT PARSE. `splitQuantity` is the one parser for quantities and lives
 * in `entity/quantity.ts`, which says why: the read side answers the composed
 * `'3.5 [SI::kg]'` and the write side wants the bare `3.5`, and two copies of
 * that rule would disagree in a way nothing catches - a quoted quantity
 * re-parses cleanly, which is how `:>> dryMass = "3.5 [SI::kg]";` reached the
 * model unnoticed. This adds only the DISPLAY half, and `unitLabel` is likewise
 * shared with the system summary so the screens agree on what a unit is called.
 */
export function displayQuantity(value: unknown): { value: string; unit: string } {
  const { number, unit } = splitQuantity(value)
  return { value: number, unit: unitLabel(unit) }
}

/**
 * One column's text for one row - a value, or a range across two.
 *
 * A RANGE IS ONE FACT. `-20 – 55 °C_abs` is what the model states; two columns
 * of numbers under near-identical headings is the same information with the
 * pairing left to the reader, on every row.
 *
 * A HALF-STATED range keeps its labels rather than being drawn as an interval:
 * `min -20` is true where `-20 – ` would imply the other end is something and
 * this screen simply did not say. Absent is not an end.
 */
/** `'-20 °C'` → `{value: '-20', unit: '°C'}`; the unit is the last word. */
function splitUnit(text: string): { value: string; unit: string } {
  const at = text.lastIndexOf(' ')
  if (at < 0) return { value: text, unit: '' }
  return { value: text.slice(0, at), unit: text.slice(at + 1) }
}

export function columnText(row: ListedPart['row'], column: PartColumn): string {
  const values = column.fields.map((field) => cellText(row[field.name], field))
  if (!column.isRange) return values.filter(Boolean).join(' ')

  // cellText has already made these read as '−20 °C'; the ends are split again
  // so a shared unit is said ONCE after the range rather than on both ends.
  const [low, high] = values
  if (low && high) {
    const left = splitUnit(low)
    const right = splitUnit(high)
    if (left.unit && left.unit === right.unit) {
      return `${left.value} – ${right.value} ${left.unit}`
    }
    // Different units on the two ends is a model problem, and showing both is
    // how it becomes visible rather than silently averaged away.
    return `${low} – ${high}`
  }
  // One end only. Named, because an interval with an open end is a different
  // statement from an interval nobody has finished writing down.
  if (low) return `min ${low}`
  if (high) return `max ${high}`
  return ''
}

export default function DefinitionsTable({
  kind,
  commonAttributes,
  listed,
  selected,
  onSelect,
  onOpen,
}: {
  kind: KindSchema
  /**
   * The attributes every part could carry, from the listing.
   *
   * Undefined keeps every column, which is what an older server's listing gets.
   */
  commonAttributes?: string[]
  listed: ListedPart[]
  selected: string
  onSelect: (id: string) => void
  /**
   * Open the full editor for this row - a DOUBLE-CLICK, Patrick's fourth
   * observation.
   *
   * The pane edits what it shows; everything else about a part is the editor at
   * `/parts/{id}`, and a double-click is how a table row has always opened the
   * thing it stands for.
   */
  onOpen: (id: string) => void
}) {
  const columns = columnsFor(kind, commonAttributes)
  // A column per rolling attribute, so this table is wider than the window on
  // any real program. Bounded and sticky-headed for the same reason the
  // DataGrids are - an unbounded container puts its sideways scrollbar below
  // every row, where it can only be reached by scrolling the header off. See
  // components/common/grid-layout.
  const { ref, height } = useFittedHeight()

  return (
    <TableContainer ref={ref} sx={{ maxHeight: height }}>
      <Table stickyHeader size="small" aria-label="Part definitions">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Role</TableCell>
            {columns.map((column) => (
              <TableCell key={column.key} sx={{ whiteSpace: 'nowrap' }}>
                {column.derived ? (
                  <Tooltip
                    title="Computed rather than stated, so the API refuses an edit to it."
                    describeChild
                  >
                    <span>{column.label} (read-only)</span>
                  </Tooltip>
                ) : (
                  column.label
                )}
                {/* The attribute's own description, from the registry, rather
                    than a column of unexplained headings. One '?' per column
                    beats a paragraph above the table, and the profile already
                    writes a sentence for every attribute it declares. */}
                {column.fields[0].help && (
                  <HelpTip title={column.fields[0].help} label={`What ${column.label} means`} />
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {listed.map(({ row, isInstance, isGeneric }) => (
            <TableRow
              key={row.qualified_name || row.id}
              hover
              selected={row.id === selected}
              onClick={() => onSelect(row.id)}
              onDoubleClick={() => onOpen(row.id)}
              sx={{ cursor: 'pointer' }}
            >
              {/* Both names on every row: the ID people compare character by
                  character, and the name they read. */}
              <TableCell sx={{ fontFamily: MONOSPACE, whiteSpace: 'nowrap' }}>{row.id}</TableCell>
              <TableCell>
                {row.name}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontFamily: MONOSPACE }}
                >
                  {row.qualified_name}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {isInstance ? (
                  <Tooltip title={INSTANCE_HELP} describeChild>
                    <Chip label="instance" size="small" color="secondary" variant="outlined" />
                  </Tooltip>
                ) : (
                  <Chip label="definition" size="small" variant="outlined" />
                )}
                {isGeneric && (
                  <Tooltip title={GENERIC_HELP} describeChild>
                    <Chip
                      label="generic"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ ml: 0.5 }}
                    />
                  </Tooltip>
                )}
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  sx={{
                    ...(column.fields[0].kind === 'type' ? { fontFamily: MONOSPACE } : {}),
                    // A range must not wrap in the middle: '-20 –' on one line
                    // and '55 °C' on the next reads as two values again.
                    ...(column.isRange ? { whiteSpace: 'nowrap' } : {}),
                  }}
                >
                  {columnText(row, column)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
